import React, { useEffect, useState } from "react";

import classNames from "classnames";
import { FieldArray, useFormikContext } from "formik";
import { Delete, DownArrow, Plus, UpArrow } from "icons";
import Pluralize from "pluralize";
import { isEmpty, isNil } from "ramda";

import { Typography, Button } from "clearsense-ui/components";
import { Input } from "clearsense-ui/components/formik";

import TemplateField from "./TemplateField";

const Hierarchy = ({ index, hierarchy, arrayHelpers }) => {
  const [previousHierarchy, setPreviousHierarchy] = useState();
  const { values, errors, setFieldValue, getFieldMeta, setFieldTouched } =
    useFormikContext();

  useEffect(() => {
    if (!hierarchy.isEdit) {
      setPreviousHierarchy(hierarchy);
    }
  }, [hierarchy]);

  const handleHierarchySave = () => {
    const hierarchyErrors = errors?.hierarchies?.[index];
    const isNameTouched = getFieldMeta(`hierarchies.${index}`).touched;
    const isfieldTouched = getFieldMeta(`hierarchies.${index}.fields`).touched;
    const hasNoError =
      isNil(hierarchyErrors) ||
      isEmpty(hierarchyErrors) ||
      hierarchyErrors?.length === 0;
    setFieldTouched(`hierarchies.${index}.name`, true);
    hierarchy.fields.map((field, fieldIndex) => {
      setFieldTouched(`hierarchies.${index}.fields.${fieldIndex}.name`);
      setFieldTouched(`hierarchies.${index}.fields.${fieldIndex}.type`);
    });
    setFieldTouched(`hierarchies.${index}.fields`, true);

    if (hasNoError && isNameTouched && isfieldTouched) {
      setFieldValue(`hierarchies.${index}.isEdit`, false);
      setFieldValue(`hierarchies.${index}.isInitial`, false);
    }
  };

  const handleCancelSave = () => {
    if (hierarchy.isInitial) {
      arrayHelpers.remove(index);
    } else {
      setFieldValue(`hierarchies.${index}.isEdit`, false);
      setFieldValue(`hierarchies.${index}.name`, previousHierarchy.name);
      setFieldValue(`hierarchies.${index}.fields`, previousHierarchy.fields);
    }
  };

  return (
    <div key={index} className="p-3 mt-4 border border-shade-light">
      {!hierarchy.isInitial && (
        <div
          className={classNames("flex justify-between items-center", {
            "border-b border-shade-light mb-4":
              hierarchy.isEdit && !hierarchy.isInitial,
          })}
        >
          <div className="pb-3">
            <Typography style="medium" type="semibold">
              {hierarchy?.name}
            </Typography>
            <Typography>
              {hierarchy.fields.length}{" "}
              {Pluralize("Field", hierarchy.fields.length)} in this hierarchy
            </Typography>
          </div>
          {hierarchy.isEdit ? (
            <Button
              icon={UpArrow}
              style="text"
              onClick={() =>
                setFieldValue(`hierarchies.${index}.isEdit`, false)
              }
            />
          ) : (
            <Button
              style="text"
              icon={DownArrow}
              disabled={values.hierarchies
                .map(hierarchy => hierarchy.isEdit)
                .includes(true)}
              onClick={() => setFieldValue(`hierarchies.${index}.isEdit`, true)}
            />
          )}
        </div>
      )}
      {hierarchy.isEdit && (
        <>
          <Input
            name={`hierarchies.${index}.name`}
            label="Hierarchy Name"
            placeholder="Enter hierarchy name"
            required
          />
          <Typography style="medium" className="mt-5">
            Fields in the Hierarchy
          </Typography>
          <FieldArray name={`hierarchies.${index}.fields`}>
            {arrayHelpers => (
              <>
                {hierarchy.fields.map((field, fieldIndex) => (
                  <TemplateField
                    key={`${index}.${fieldIndex}`}
                    index={fieldIndex}
                    field={field}
                    hierarchy={hierarchy}
                    hierarchyIndex={index}
                    arrayHelpers={arrayHelpers}
                  />
                ))}
                <Button
                  label="Create Field"
                  icon={Plus}
                  style="secondary"
                  className="mt-4"
                  disabled={hierarchy?.fields
                    .map(field => field?.isEdit)
                    .includes(true)}
                  onClick={() => {
                    arrayHelpers.push({
                      name: "",
                      type: "",
                      isEdit: true,
                      isInitial: true,
                    });
                  }}
                />
              </>
            )}
          </FieldArray>
          <div className=" flex gap-1 mt-4 border-t border-shade-light pt-4">
            <Button label="Save Hierarchy" onClick={handleHierarchySave} />
            <Button
              label="Cancel"
              style="secondary"
              disabled={values.hierarchies.length <= 1}
              onClick={handleCancelSave}
            />
            {!hierarchy.isInitial && (
              <Button
                icon={Delete}
                style="secondary"
                className="text-red-700"
                onClick={() => arrayHelpers.remove(index)}
                disabled={values.hierarchies.length === 1}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Hierarchy;
